import { Ref, shallowReadonly, shallowRef } from "vue";

/**
 * A composable for Flux/Redux-like component state management. Consolidate complex state logic into a single, pure function -- the `reducer`.
 *
 * @see {@link https://redux.js.org/introduction/core-concepts} for the core concepts of this idea
 *
 * @param reducer A pure function that receives the current state, some action (usually a message describing an event that should cause a state update), and returns an updated copy of the state. Should NOT mutate the state given to it.
 * @param initialState The initial state value
 * @returns An object containing `state` (a readonly-ref holding the current state) and dispatch (a function that accepts actions that get passed to the reducer and update the state)
 */
export function useReducer<TState, TAction>(reducer: (state: TState, action: TAction) => TState, initialState: TState): {
	dispatch: (action: TAction) => void;
	state: Readonly<Ref<Readonly<TState>>>;
} {
	const state = shallowRef(initialState);

	function dispatch(action: TAction) {
		state.value = reducer(state.value, action);
	}

	return { state: shallowReadonly(state), dispatch };
}
